"use client";

import Image from "next/image";
import Link from "next/link";
import { LogOut, User } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

const fallbackAvatar = "/assets/icons/profile.svg";

export default function ProfileDropdown({ mobile = false }: { mobile?: boolean }) {
  const t = useTranslations("navbar");
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={mobile
            ? "flex w-full items-center gap-2 rounded-md p-1 text-black outline-none focus-visible:ring-2 focus-visible:ring-[#CD2834]"
            : "rounded-full p-1 outline-none transition hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/70"}
          aria-label={t("open_profile_menu")}
        >
          <Image
            src={user.avatar || fallbackAvatar}
            alt={user.name}
            width={36}
            height={36}
            unoptimized
            className="h-9 w-9 rounded-full object-cover"
          />
          {mobile && <span>{t("profile")}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={mobile ? "start" : "end"} className="w-64">
        <DropdownMenuLabel>
          <div className="flex items-center gap-3 py-1">
            <Image
              src={user.avatar || fallbackAvatar}
              alt={user.name}
              width={36}
              height={36}
              unoptimized
              className="h-9 w-9 rounded-full object-cover"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
              <p className="truncate text-xs font-normal text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link href={`${process.env.NEXT_PUBLIC_SSO_URL}/user/profile`} className="flex w-full items-center">
            <User className="mr-2 h-4 w-4" />
            <span>{t("profile")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link href="/api/auth/logout" className="flex w-full items-center">
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t("logout")}</span>
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
